import React from "react";
import { Box, Typography, Button, IconButton, Tooltip, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { STATUS_CONFIG, CALENDAR_EVENT_TYPES } from "../constants";
import { to12Hour } from "../utils";
import { formatServiceDate } from "../../../utils/dateUtils";
import { SERVICE_TYPES } from "../../Servicios/constants";
import { isInternalCalendarEvent, isGhostOccurrence, getGhostTransitionLabel } from "../utils";

const GHOST_COLOR = "#94a3b8";
const GHOST_TEXT = "#64748b";

const TYPE_COLOR = {
  MIP: "#1D4E89",
  TICKET: "#B45309",
  NEBULIZACION: "#0F766E",
  MICROINYECCION: "#6D28D9",
  MOSCA: "#3F6212",
  ABEJA: "#A16207",
  EQUIPO: "#334155",
  CHOQUE: "#B91C1C",
  SUPERVISION: "#0369A1",
  EVENTUAL: "#9A3412",
  OTRO: "#57534E",
};

const typeLabel = (type) =>
  SERVICE_TYPES.find((t) => t.value === type)?.label || type || "Servicio";

export const ServiceDetailsPanel = ({
  service,
  onEdit,
  onDelete,
  onClose,
  onCreatePestReport,
  onViewPestReport,
  loading,
}) => {
  if (!service) return null;

  const status = STATUS_CONFIG[service.status] || {
    color: "#64748B",
    label: service.status,
  };
  const type = service.type || service.eventType || "";
  const isEvent = isInternalCalendarEvent(service);
  const isGhost = isGhostOccurrence(service);
  const eventTypeMeta = CALENDAR_EVENT_TYPES.find((t) => t.value === service.eventType);
  const accentColor = isEvent
    ? eventTypeMeta?.color || "#7c3aed"
    : TYPE_COLOR[String(type).toUpperCase()] || "#1D4E89";
  const color = isGhost ? GHOST_COLOR : accentColor;
  const clientName = service.client?.name || service.client || "";
  const branchName = service.branch?.name || service.branch || "";
  const place = isEvent
    ? service.title || eventTypeMeta?.label || "Evento"
    : [clientName, branchName].filter(Boolean).join(" · ") || "Sin sucursal";
  const techName = service.technician?.name || service.technician || "Sin asignar";
  const extraTechs = (service.technicians || [])
    .map((t) => t?.name || t)
    .filter(Boolean);
  const techLabel =
    extraTechs.length > 1 ? extraTechs.join(", ") : techName;
  const completed = service.status === "COMPLETED";
  const hour = service.hour ? to12Hour(service.hour) : "Sin hora";
  const dateLabel = formatServiceDate(service.isoDate, "es-MX", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        opacity: isGhost ? 0.92 : 1,
        filter: isGhost ? "grayscale(0.35)" : "none",
      }}
    >
      <Box
        sx={{
          height: 3,
          bgcolor: color,
          flexShrink: 0,
          ...(isGhost ? { backgroundImage: "repeating-linear-gradient(90deg, #94a3b8 0 8px, #cbd5e1 8px 16px)" } : {}),
        }}
      />

      <Box sx={{ px: 1.75, pt: 1.4, pb: 1.5 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box minWidth={0}>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: "-0.04em",
                lineHeight: 1,
                color: "#111",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {hour}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 12, color: "#8A8F98" }}>
              {dateLabel}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <Box
              sx={{
                px: 0.85,
                py: 0.25,
                borderRadius: "999px",
                fontSize: 10.5,
                fontWeight: 700,
                bgcolor: `${status.color}14`,
                color: status.color,
              }}
            >
              {isGhost ? "Fantasma" : status.label}
            </Box>
            {onClose ? (
              <IconButton size="small" onClick={onClose} sx={{ color: "#9CA3AF", p: 0.3 }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </Box>
        </Box>

        <Typography
          sx={{
            mt: 1.35,
            fontSize: 15,
            fontWeight: 600,
            color: "#111",
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
          }}
        >
          {place}
        </Typography>
        {service.branch?.address || (isEvent && service.location) ? (
          <Typography sx={{ mt: 0.3, fontSize: 12.5, color: "#6B7280", lineHeight: 1.4 }}>
            {service.branch?.address || service.location}
          </Typography>
        ) : null}

        <Box
          sx={{
            mt: 1.25,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.6,
          }}
        >
          <Box
            sx={{
              px: 0.8,
              py: 0.25,
              borderRadius: "6px",
              bgcolor: `${color}12`,
              color,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {isEvent ? eventTypeMeta?.label || "Evento" : typeLabel(type)}
          </Box>
          <Box
            sx={{
              px: 0.8,
              py: 0.25,
              borderRadius: "6px",
              bgcolor: isEvent ? "#F5F3FF" : "#F3F4F6",
              color: isEvent ? "#6D28D9" : "#4B5563",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {isEvent ? "Evento" : "Servicio"}
          </Box>
          {isGhost ? (
            <Box
              sx={{
                px: 0.8,
                py: 0.25,
                borderRadius: "6px",
                border: "1px dashed #94a3b8",
                color: "#64748b",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              Mes siguiente
            </Box>
          ) : null}
          <Box
            sx={{
              px: 0.8,
              py: 0.25,
              borderRadius: "6px",
              bgcolor: "#F3F4F6",
              color: "#4B5563",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {techLabel}
          </Box>
          {service.unit ? (
            <Box
              sx={{
                px: 0.8,
                py: 0.25,
                borderRadius: "6px",
                bgcolor: "#F3F4F6",
                color: "#4B5563",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {service.unit}
            </Box>
          ) : null}
        </Box>

        {isGhost ? (
          <Box
            sx={{
              mt: 1.25,
              px: 1,
              py: 0.85,
              borderRadius: "8px",
              border: "1px dashed #cbd5e1",
              bgcolor: "#f8fafc",
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: GHOST_TEXT, fontWeight: 700, lineHeight: 1.45 }}>
              Previsualización — solo lectura
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 12, color: GHOST_TEXT, lineHeight: 1.45 }}>
              {getGhostTransitionLabel(service)}. Hasta entonces no se puede editar ni crear reporte.
            </Typography>
          </Box>
        ) : null}

        {service.notes ? (
          <Typography
            sx={{
              mt: 1.25,
              fontSize: 12.5,
              color: "#4B5563",
              lineHeight: 1.45,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {service.notes}
          </Typography>
        ) : null}

        {!isGhost ? (
          <Stack direction="row" spacing={0.7} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={onEdit}
              disabled={loading || (!isEvent && completed)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12.5,
                borderRadius: "8px",
                bgcolor: "#111",
                px: 1.4,
                "&:hover": { bgcolor: "#222" },
              }}
            >
              Editar
            </Button>
            {isEvent ? null : completed && onViewPestReport ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={onViewPestReport}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12.5,
                  borderRadius: "8px",
                  borderColor: "#E5E7EB",
                  color: "#111",
                  "&:hover": { borderColor: "#111", bgcolor: "transparent" },
                }}
              >
                Reporte
              </Button>
            ) : onCreatePestReport ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<BugReportOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={onCreatePestReport}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12.5,
                  borderRadius: "8px",
                  borderColor: "#E5E7EB",
                  color: "#111",
                  "&:hover": { borderColor: "#111", bgcolor: "transparent" },
                }}
              >
                Reporte
              </Button>
            ) : null}
            <Box flex={1} />
            <Tooltip title={!isEvent && completed ? "Completado" : "Eliminar"}>
              <span>
                <IconButton
                  size="small"
                  onClick={onDelete}
                  disabled={loading || (!isEvent && completed)}
                  sx={{
                    color: "#9CA3AF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    width: 32,
                    height: 32,
                    "&:hover": { color: "#B91C1C", borderColor: "#FECACA" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
};
